import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "./constant_api";
import { useLocation, useSearchParams } from "react-router-dom";

const SelectBankAccountModal = ({
  isOpen,
  currentUserId,
  onClose,
  onSubmit,
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // ---- resolved org id list (from URL, or single fallback) ----
  const [orgIds, setOrgIds] = useState([]);

  // ---- org multi-select dropdown state ----
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);

  // ---- per-org cached data: { [orgId]: { name, accounts, users } } ----
  // IMPORTANT: every read from this object MUST be keyed by orgId.
  // Nothing here is ever shared/merged across orgs.
  const [orgData, setOrgData] = useState({});

  // ---- per-org selections: { [orgId]: { accountIds: [], userIds: [] } } ----
  const [selections, setSelections] = useState({});

  // ---- per-org loading/error flags: { [orgId]: bool | string } ----
  const [orgAccountsLoading, setOrgAccountsLoading] = useState({});
  const [orgUsersLoading, setOrgUsersLoading] = useState({});
  const [orgAccountsError, setOrgAccountsError] = useState({});
  const [orgUsersError, setOrgUsersError] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // ---- parse OrganisationIDs from URL on open, then fetch org names ----
  useEffect(() => {
    if (!isOpen) return;

    let parsedIds = [];
    const params = new URLSearchParams(location.search);
    const rawParam =
      params.get("OrganisationIDs") || searchParams.get("OrganisationIDs");

    if (rawParam) {
      try {
        const parsed = JSON.parse(rawParam);
        if (Array.isArray(parsed)) {
          parsedIds = parsed.filter(Boolean);
        }
      } catch (e) {
        console.error("Invalid OrganisationIDs in URL:", rawParam, e);
        if (rawParam.includes(",") || rawParam.includes("[")) {
          parsedIds = rawParam
            .replace(/[[\]"]/g, "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
      }
    }

    if (parsedIds.length === 0) {
      const singleId =
        params.get("OrganisationID") || searchParams.get("OrganisationID");
      if (singleId) parsedIds = [singleId];
    }

    // reset everything for a fresh open
    parsedIds = [...new Set(parsedIds)]; // dedupe defensively
    setOrgIds(parsedIds);
    setSelectedOrgIds([]);
    setOrgData({});
    setSelections({});
    setOrgAccountsLoading({});
    setOrgUsersLoading({});
    setOrgAccountsError({});
    setOrgUsersError({});
    setError("");
    setOrgDropdownOpen(false);

    if (parsedIds.length > 0 && currentUserId) {
      fetchOrgNamesForUser(currentUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location.search]);

  // ---- close org dropdown on outside click ----
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---- fetch org names for the current user in ONE call, build ID->Name map ----
  const fetchOrgNamesForUser = async (userId) => {
    if (!userId) return;
    try {
      const response = await axios.get(
        `${BASE_URL}/organisation`,
        { params: { UserId: userId } }
      );
      const list = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];
      setOrgData((prev) => {
        const next = { ...prev };
        list.forEach((org) => {
          const orgId = org.OrganisationID;
          if (!orgId) return;
          next[orgId] = {
            ...(next[orgId] || {}),
            name: org.Name || org.LegalName || next[orgId]?.name || orgId,
          };
        });
        return next;
      });
    } catch (err) {
      console.error("Error fetching organisation names:", err);
      // Silent fallback: org rows just show the raw ID.
    }
  };

  // ---- fetch helpers, scoped per org (always keyed by the passed orgId) ----
  // orgId is captured by value (function argument), so even when these run
  // concurrently for multiple orgs, each call updates only its own key.
  const fetchBankAccounts = async (orgId) => {
    setOrgAccountsLoading((prev) => ({ ...prev, [orgId]: true }));
    setOrgAccountsError((prev) => ({ ...prev, [orgId]: "" }));
    try {
      const response = await axios.get(
        `${BASE_URL}/organisation/get-bank-accounts`,
        { params: { OrganisationID: orgId } }
      );

      if (response?.data?.success) {
        const accounts = response.data.data || [];
        const fetchedName = response.data.organisationName;
        setOrgData((prev) => ({
          ...prev,
          [orgId]: {
            ...(prev[orgId] || {}),
            name: fetchedName || prev[orgId]?.name || orgId,
            accounts,
          },
        }));
      } else {
        setOrgAccountsError((prev) => ({
          ...prev,
          [orgId]: response?.data?.message || "Failed to fetch bank accounts",
        }));
      }
    } catch (err) {
      console.error(`Error fetching bank accounts for org ${orgId}:`, err);
      setOrgAccountsError((prev) => ({
        ...prev,
        [orgId]:
          err?.response?.data?.message ||
          "Something went wrong while fetching accounts",
      }));
    } finally {
      setOrgAccountsLoading((prev) => ({ ...prev, [orgId]: false }));
    }
  };

  const fetchXeroUsers = async (orgId) => {
    setOrgUsersLoading((prev) => ({ ...prev, [orgId]: true }));
    setOrgUsersError((prev) => ({ ...prev, [orgId]: "" }));
    try {
      const response = await axios.get(
        `${BASE_URL}/organisation/get-xero-users`,
        { params: { OrganisationID: orgId } }
      );

      if (response?.data?.success) {
        const fetchedUsers = response.data.users || [];
        const filteredUsers = currentUserId
          ? fetchedUsers.filter((u) => u.UserID !== currentUserId)
          : fetchedUsers;
        setOrgData((prev) => ({
          ...prev,
          [orgId]: { ...(prev[orgId] || {}), users: filteredUsers },
        }));
      } else {
        setOrgUsersError((prev) => ({
          ...prev,
          [orgId]: response?.data?.message || "Failed to fetch users",
        }));
      }
    } catch (err) {
      console.error(`Error fetching Xero users for org ${orgId}:`, err);
      setOrgUsersError((prev) => ({
        ...prev,
        [orgId]:
          err?.response?.data?.message ||
          "Something went wrong while fetching users",
      }));
    } finally {
      setOrgUsersLoading((prev) => ({ ...prev, [orgId]: false }));
    }
  };

  // ---- org dropdown (multi-select) handlers ----
  // Selecting an org immediately kicks off its accounts + users fetch
  // (each call strictly scoped to that org's own key), so everything
  // renders on the single combined screen without a "Next" step.
  const toggleOrgSelected = (orgId) => {
    setSelectedOrgIds((prev) => {
      const isSelected = prev.includes(orgId);
      const next = isSelected ? prev.filter((id) => id !== orgId) : [...prev, orgId];
      if (!isSelected) {
        if (!orgData[orgId]?.accounts) fetchBankAccounts(orgId);
        if (!orgData[orgId]?.users) fetchXeroUsers(orgId);
      }
      return next;
    });
    setError("");
  };

  const toggleSelectAllOrgs = () => {
    if (selectedOrgIds.length === orgIds.length) {
      setSelectedOrgIds([]);
    } else {
      setSelectedOrgIds([...orgIds]);
      orgIds.forEach((orgId) => {
        if (!orgData[orgId]?.accounts) fetchBankAccounts(orgId);
        if (!orgData[orgId]?.users) fetchXeroUsers(orgId);
      });
    }
    setError("");
  };

  const removeSelectedOrg = (orgId) => {
    setSelectedOrgIds((prev) => prev.filter((id) => id !== orgId));
  };

  // ---- account/user selection handlers (always scoped by explicit orgId) ----
  const getSelection = (orgId) => selections[orgId] || { accountIds: [], userIds: [] };

  const setOrgSelection = (orgId, patch) => {
    setSelections((prev) => ({
      ...prev,
      [orgId]: { ...getSelection(orgId), ...patch },
    }));
  };

  const handleAccountCheckboxChange = (orgId, accountId) => {
    const current = getSelection(orgId);
    const ids = current.accountIds.includes(accountId)
      ? current.accountIds.filter((id) => id !== accountId)
      : [...current.accountIds, accountId];
    setOrgSelection(orgId, { accountIds: ids });
  };

  const handleSelectAllAccounts = (orgId) => {
    const current = getSelection(orgId);
    const accounts = orgData[orgId]?.accounts || [];
    const ids =
      current.accountIds.length === accounts.length ? [] : accounts.map((a) => a.AccountID);
    setOrgSelection(orgId, { accountIds: ids });
  };

  const handleUserCheckboxChange = (orgId, userId) => {
    const current = getSelection(orgId);
    const ids = current.userIds.includes(userId)
      ? current.userIds.filter((id) => id !== userId)
      : [...current.userIds, userId];
    setOrgSelection(orgId, { userIds: ids });
  };

  const handleSelectAllUsers = (orgId) => {
    const current = getSelection(orgId);
    const users = orgData[orgId]?.users || [];
    const ids = current.userIds.length === users.length ? [] : users.map((u) => u.UserID);
    setOrgSelection(orgId, { userIds: ids });
  };

  // Every selected org must have at least one account selected before submit.
  const allOrgsHaveAccountSelected =
    selectedOrgIds.length > 0 &&
    selectedOrgIds.every((orgId) => getSelection(orgId).accountIds.length > 0);

  const anyAccountsLoading = selectedOrgIds.some((id) => orgAccountsLoading[id]);
  const anyUsersLoading = selectedOrgIds.some((id) => orgUsersLoading[id]);

  const handleSubmit = async () => {
    if (selectedOrgIds.length === 0) {
      setError("Please select at least one organisation");
      return;
    }
    if (!allOrgsHaveAccountSelected) {
      setError("Please select at least one account for every selected organisation");
      return;
    }

    setSubmitting(true);
    setError("");

    // Build backend payload grouped by organisation — one entry per org,
    // each carrying only that org's own selected accounts/users.
    const Data = selectedOrgIds.map((orgId) => ({
      OrganisationID: orgId,
      Accounts: selections[orgId]?.accountIds || [],
      Users: [...(selections[orgId]?.userIds || []), currentUserId],
    }));

    try {
      const syncResponse = await axios.post(
        `${BASE_URL}/organisation/sync-new-data`,
        { Data, CurrentUserID: currentUserId }
      );

      if (syncResponse?.data?.success !== true) {
        setError(syncResponse?.data?.message || "Full sync failed. Data was not deleted.");
        return;
      }

      const removeResponse = await axios.post(
        `${BASE_URL}/organisation/remove-selected-data`,
        { Data, CurrentUserID: currentUserId }
      );

      if (removeResponse?.data?.success !== true) {
        setError(removeResponse?.data?.message || "Failed to remove unselected data.");
        return;
      }

      onSubmit?.(Data, removeResponse?.data);
      handleClose();
      window.location.href = "/query";
    } catch (err) {
      console.error("Error submitting selected accounts/users:", err);
      setError(err?.response?.data?.message || "Failed to submit selected accounts/users");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedOrgIds([]);
    setOrgData({});
    setSelections({});
    setOrgAccountsLoading({});
    setOrgUsersLoading({});
    setOrgAccountsError({});
    setOrgUsersError({});
    setError("");
    setOrgDropdownOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  const totalAccountsSelected = selectedOrgIds.reduce(
    (sum, orgId) => sum + getSelection(orgId).accountIds.length,
    0
  );
  const totalUsersSelected = selectedOrgIds.reduce(
    (sum, orgId) => sum + getSelection(orgId).userIds.length,
    0
  );

  const orgLabel = (orgId) =>
    orgData[orgId]?.name && orgData[orgId]?.name !== orgId ? orgData[orgId].name : orgId;

  return (
    <div className="bam-overlay">
      <div className="bam-modal">
        <div className="bam-header">
          <div className="bam-header-titlewrap">
            <h4 className="bam-title">Select Bank Accounts &amp; Users</h4>
            <p className="bam-subtitle">
              Choose organisations, then pick accounts and users for each
            </p>
          </div>
          <button type="button" className="bam-close-btn" onClick={handleClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="bam-body">
          {/* ---- Organisation multi-select dropdown ---- */}
          <div className="bam-field">
            <label className="bam-field-label">Organisations</label>

            {orgIds.length === 0 ? (
              <div className="bam-empty">No organisations found in the link.</div>
            ) : (
              <div className="bam-org-dropdown" ref={orgDropdownRef}>
                <button
                  type="button"
                  className={`bam-org-dropdown-trigger ${orgDropdownOpen ? "is-open" : ""}`}
                  onClick={() => setOrgDropdownOpen((o) => !o)}
                >
                  {selectedOrgIds.length === 0 ? (
                    <span className="bam-org-dropdown-placeholder">Select organisation(s)</span>
                  ) : (
                    <div className="bam-org-chips">
                      {selectedOrgIds.map((orgId) => (
                        <span className="bam-org-chip" key={orgId}>
                          {orgLabel(orgId)}
                          <button
                            type="button"
                            className="bam-org-chip-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSelectedOrg(orgId);
                            }}
                            aria-label={`Remove ${orgLabel(orgId)}`}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <span className={`bam-org-dropdown-arrow ${orgDropdownOpen ? "is-open" : ""}`}>
                    &#9662;
                  </span>
                </button>

                {orgDropdownOpen && (
                  <div className="bam-org-dropdown-panel">
                    <div className="bam-select-all-row bam-select-all-row--panel">
                      <label className="bam-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedOrgIds.length === orgIds.length && orgIds.length > 0}
                          onChange={toggleSelectAllOrgs}
                        />
                        <span>Select All</span>
                      </label>
                    </div>
                    <div className="bam-org-dropdown-list">
                      {orgIds.map((orgId) => (
                        <label className="bam-checkbox-label bam-org-dropdown-item" key={orgId}>
                          <input
                            type="checkbox"
                            checked={selectedOrgIds.includes(orgId)}
                            onChange={() => toggleOrgSelected(orgId)}
                          />
                          <div className="bam-item-info">
                            <span className="bam-item-name">{orgLabel(orgId)}</span>
                            {orgData[orgId]?.name && orgData[orgId]?.name !== orgId && (
                              <span className="bam-item-meta">{orgId}</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ---- Combined accounts + users sections, one per selected org ---- */}
          {selectedOrgIds.length === 0 ? (
            <div className="bam-empty-state">
              <div className="bam-empty-state-icon">🏦</div>
              <p>Select one or more organisations above to see their bank accounts and users.</p>
            </div>
          ) : (
            <div className="bam-org-sections">
              {selectedOrgIds.map((orgId) => {
                const accounts = orgData[orgId]?.accounts || [];
                const users = orgData[orgId]?.users || [];
                const selection = getSelection(orgId);
                const accLoading = !!orgAccountsLoading[orgId];
                const usrLoading = !!orgUsersLoading[orgId];
                const accError = orgAccountsError[orgId];
                const usrError = orgUsersError[orgId];
                const initials = orgLabel(orgId).slice(0, 2).toUpperCase();

                return (
                  <div className="bam-org-section" key={orgId}>
                    <div className="bam-org-section-header">
                      <span className="bam-org-avatar">{initials}</span>
                      <h5 className="bam-org-section-title">{orgLabel(orgId)}</h5>
                      <span
                        className={`bam-org-section-count ${
                          selection.accountIds.length > 0 ? "bam-org-section-count--done" : ""
                        }`}
                      >
                        {selection.accountIds.length}/{accounts.length} accounts
                      </span>
                    </div>

                    <div className="bam-org-section-body">
                      {/* ---- Accounts sub-section ---- */}
                      <div className="bam-subsection">
                        <div className="bam-subsection-heading">
                          <span className="bam-subsection-icon">💳</span>
                          <span>Bank Accounts</span>
                        </div>

                        {accLoading && (
                          <div className="bam-loading">
                            <div className="bam-spinner"></div>
                            <span>Loading accounts...</span>
                          </div>
                        )}

                        {!accLoading && accError && <div className="bam-error">{accError}</div>}

                        {!accLoading && !accError && accounts.length === 0 && (
                          <div className="bam-empty">No bank accounts found.</div>
                        )}

                        {!accLoading && !accError && accounts.length > 0 && (
                          <>
                            <div className="bam-select-all-row bam-select-all-row--nested">
                              <label className="bam-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={
                                    selection.accountIds.length === accounts.length &&
                                    accounts.length > 0
                                  }
                                  onChange={() => handleSelectAllAccounts(orgId)}
                                />
                                <span>Select All</span>
                              </label>
                            </div>

                            <div className="bam-list">
                              {accounts.map((account) => (
                                <div className="bam-row" key={account.AccountID}>
                                  <label className="bam-checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={selection.accountIds.includes(account.AccountID)}
                                      onChange={() =>
                                        handleAccountCheckboxChange(orgId, account.AccountID)
                                      }
                                    />
                                    <div className="bam-item-info">
                                      <span className="bam-item-name">{account.Name}</span>
                                      <span className="bam-item-meta">
                                        {account.BankAccountType} &middot; {account.CurrencyCode}
                                        {account.BankAccountNumber
                                          ? ` \u00b7 ****${account.BankAccountNumber.slice(-4)}`
                                          : ""}
                                      </span>
                                    </div>
                                    <span
                                      className={`bam-status-badge bam-status-${(
                                        account.Status || ""
                                      ).toLowerCase()}`}
                                    >
                                      {account.Status}
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      {/* ---- Users sub-section ---- */}
                      <div className="bam-subsection">
                        <div className="bam-subsection-heading">
                          <span className="bam-subsection-icon">👤</span>
                          <span>Users</span>
                          <span className="bam-subsection-count">
                            {selection.userIds.length}/{users.length}
                          </span>
                        </div>

                        {usrLoading && (
                          <div className="bam-loading">
                            <div className="bam-spinner"></div>
                            <span>Loading users...</span>
                          </div>
                        )}

                        {!usrLoading && usrError && <div className="bam-error">{usrError}</div>}

                        {!usrLoading && !usrError && users.length === 0 && (
                          <div className="bam-empty">No users found for this organisation.</div>
                        )}

                        {!usrLoading && !usrError && users.length > 0 && (
                          <>
                            <div className="bam-select-all-row bam-select-all-row--nested">
                              <label className="bam-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={
                                    selection.userIds.length === users.length && users.length > 0
                                  }
                                  onChange={() => handleSelectAllUsers(orgId)}
                                />
                                <span>Select All</span>
                              </label>
                            </div>

                            <div className="bam-list">
                              {users.map((user) => (
                                <div className="bam-row" key={user.UserID}>
                                  <label className="bam-checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={selection.userIds.includes(user.UserID)}
                                      onChange={() => handleUserCheckboxChange(orgId, user.UserID)}
                                    />
                                    <div className="bam-item-info">
                                      <span className="bam-item-name">
                                        {user.FirstName} {user.LastName}
                                      </span>
                                      <span className="bam-item-meta">
                                        {user.EmailAddress}
                                        {user.OrganisationRole
                                          ? ` \u00b7 ${user.OrganisationRole}`
                                          : ""}
                                      </span>
                                    </div>
                                    {user.IsSubscriber && (
                                      <span className="bam-status-badge bam-status-active">
                                        Subscriber
                                      </span>
                                    )}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {error && <div className="bam-error bam-error--global">{error}</div>}
        </div>

        <div className="bam-footer">
          <div className="bam-selected-summary">
            <span className="bam-selected-count">
              {selectedOrgIds.length} organisation(s)
            </span>
            <span className="bam-selected-dot">&middot;</span>
            <span className="bam-selected-count">{totalAccountsSelected} account(s)</span>
            <span className="bam-selected-dot">&middot;</span>
            <span className="bam-selected-count">{totalUsersSelected} user(s)</span>
          </div>
          <div className="bam-footer-actions">
            <button
              type="button"
              className="bam-btn bam-btn-secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bam-btn bam-btn-primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                anyAccountsLoading ||
                anyUsersLoading ||
                selectedOrgIds.length === 0 ||
                !allOrgsHaveAccountSelected
              }
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectBankAccountModal;